import type { LatitudeLongitude, ManualAdjustments, OrderFeature } from '@aloft/toolkit-shared';
import {
  AloftArea,
  AloftDate,
  AloftPrice,
  EXPLICIT_UNKNOWN_SOURCE_DISPLAY,
  ORDER_FEATURES,
  addressToString,
  formatShortenedPropertyType,
  getAbbreviatedCarportGarage,
  getAduBathsAbbreviation,
  getBasementAtticTypeAbbreviation,
  getPoolTypeAbbreviation,
  getPresentFeatureLabels,
  getSiteDisplay,
  isExplicitUnknownSelectedForFieldPath,
  orUndefined,
  schAloftPrice,
} from '@aloft/toolkit-shared';

import type { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import type { CompPropertyDataState, SubjectPropertyDataState } from '../../../redux/types.ts';
import type { SalesComparisonAdjustmentKey, SalesComparisonGridColumnData } from '../types.ts';
import type { ImprovementsPathPrefix } from './utils.ts';
import {
  formatFeatureArea,
  getDistanceToSubject,
  getExplicitZeroAdjustments,
  getFoundationDisplay,
  getPropertyImageWithFallback,
  resolveOrderKeyedAreaFeaturePdfCell,
  resolveOrderKeyedFeaturePdfCell,
} from './utils.ts';

export const convertSalesComparisonDataToPDFGridColumn = async (
  id: string,
  compSequence: string[],
  data?: CompPropertyDataState['compData'] | SubjectPropertyDataState['subjectData'],
  adjustments?: CompAdjustments,
  manualAdjustments?: ManualAdjustments & { id?: string },
  subjectCoords?: LatitudeLongitude,
  entityListingPhotos?: Blob[],
  effectiveYear?: number,
  orderFeatures?: OrderFeature[],
  includeCostApproach = true,
): Promise<SalesComparisonGridColumnData | null> => {
  if (!data) return null;

  const {
    editableData: { propertyIdentification, transaction, site },
    readonlyData: { backupListingData },
  } = data;

  let { improvements } = data.editableData;
  const arvEditableData =
    id === 'subjectArv'
      ? (data as SubjectPropertyDataState['subjectData']).editableData.arv
      : undefined;
  if (arvEditableData) {
    improvements = arvEditableData.improvements;
  }

  const { selectedSources } = data;
  const improvementsPathPrefix: ImprovementsPathPrefix =
    id === 'subjectArv' ? 'editableData.arv.improvements' : 'editableData.improvements';

  const unknownLabel = (suffix: string) =>
    isExplicitUnknownSelectedForFieldPath(selectedSources, `${improvementsPathPrefix}.${suffix}`)
      ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
      : undefined;

  const address = orUndefined(addressToString)(propertyIdentification?.address) ?? '';

  const propertyImage: Blob | string | undefined =
    entityListingPhotos?.[0] ??
    (await getPropertyImageWithFallback(
      backupListingData?.listing.Media?.[0]?.MediaURL,
      propertyIdentification?.coordinates,
    ));

  const distanceToSubject = getDistanceToSubject(
    subjectCoords,
    propertyIdentification?.coordinates,
  );

  const actualAge =
    effectiveYear && improvements.age !== undefined
      ? `${improvements.age} yrs (${effectiveYear - improvements.age})`
      : undefined;

  const aboveGradeHalfBaths = improvements.halfBaths || 0;
  const belowGradeHalfBaths =
    improvements.basement?.hasFeature && improvements.basement.halfBaths
      ? improvements.basement.halfBaths
      : 0;

  const aboveGradeFullBaths = improvements.fullBaths || 0;
  const belowGradeFullBaths =
    improvements.basement?.hasFeature && improvements.basement.fullBaths
      ? improvements.basement.fullBaths
      : 0;

  const basementPdfTreatAsNone =
    improvements.basement?.hasFeature === false ||
    (orderFeatures !== undefined &&
      orderFeatures.includes('basement') &&
      improvements.basement?.hasFeature !== true);

  let basementFullBaths = '';
  let basementHalfBaths = '';

  if (basementPdfTreatAsNone) {
    basementFullBaths = 'None';
    basementHalfBaths = 'None';
  } else if (improvements.basement?.hasFeature) {
    basementFullBaths = unknownLabel('basement.fullBaths') ?? belowGradeFullBaths.toString();
    basementHalfBaths = unknownLabel('basement.halfBaths') ?? belowGradeHalfBaths.toString();
  }

  const explicitZeroAdjustments: Partial<Record<SalesComparisonAdjustmentKey, true>> | undefined =
    getExplicitZeroAdjustments(manualAdjustments);

  const siteDisplay = getSiteDisplay(site);
  const otherAdjustments =
    orUndefined(schAloftPrice.parse)(
      (arvEditableData ?? data.editableData).manualAdjustments?.other,
    ) ?? adjustments?.other?.adjustment;

  return {
    imageUrl: propertyImage ?? '',
    address,
    dataSource:
      typeof improvements?.featureGridDataSources === 'string'
        ? improvements.featureGridDataSources
        : '',
    proximity: distanceToSubject ? `${distanceToSubject} miles` : '',
    proximityMiles: distanceToSubject,
    listingStatus: transaction.status,
    salesPrice:
      transaction.status === 'SettledSale'
        ? orUndefined(schAloftPrice.parse)(transaction.price)
        : undefined,
    listingPrice: orUndefined(schAloftPrice.parse)(
      transaction.status === 'SettledSale' ? transaction.originalListPrice : transaction.price,
    ),
    dom: (transaction.daysOnMarket ?? '').toString(),
    contractDate: transaction.contractDate
      ? AloftDate.fromIsoString(`${transaction.contractDate}-01`).format('MM/yy')
      : '',
    saleDate: transaction.saleDate
      ? AloftDate.fromIsoString(`${transaction.saleDate}-01`).format('MM/yy')
      : '',
    siteSize: site.area ? AloftArea.schema.parse(site.area).toString(true) : '',
    siteInfluence: siteDisplay.influence,
    view: siteDisplay.view,
    quality: `Q${improvements.uadQuality ?? '-'}${getFoundationDisplay(improvements)}`,
    condition: `C${improvements.uadCondition ?? '-'}`,
    gla: improvements.gla
      ? AloftArea.fromNumber(improvements.gla.unitAmount, improvements.gla.unit).toString(true)
      : '',
    bedCount: improvements.bedrooms ? improvements.bedrooms.toString() : '',
    fullBaths: aboveGradeFullBaths.toString(),
    halfBaths: aboveGradeHalfBaths.toString(),
    bathAboveGrade: `${aboveGradeFullBaths}.${aboveGradeHalfBaths}`,
    basementBaths: basementPdfTreatAsNone
      ? 'None'
      : `${belowGradeFullBaths}.${belowGradeHalfBaths}`,
    additionalFeatures: {
      basementTotal: basementPdfTreatAsNone
        ? 'None'
        : (unknownLabel('basement.totalArea') ??
          [
            getBasementAtticTypeAbbreviation(improvements.basement?.type),
            formatFeatureArea({
              area: improvements.basement?.totalArea?.unitAmount,
              unit: improvements.basement?.totalArea?.unit,
              hasFeature: improvements.basement?.hasFeature,
            }),
          ]
            .filter(Boolean)
            .join('; ')),
      basementFinished:
        basementPdfTreatAsNone || improvements.basement?.type === 'unfinished'
          ? 'None'
          : (unknownLabel('basement.finishedArea') ??
            formatFeatureArea({
              area: improvements.basement?.finishedArea?.unitAmount,
              unit: improvements.basement?.finishedArea?.unit,
              hasFeature: improvements.basement?.hasFeature,
            })),
      basementArea: basementPdfTreatAsNone
        ? 'None'
        : [
            getBasementAtticTypeAbbreviation(improvements.basement?.type),
            [
              unknownLabel('basement.totalArea') ??
                formatFeatureArea({
                  area: improvements.basement?.totalArea?.unitAmount,
                  unit: improvements.basement?.totalArea?.unit,
                  hasFeature: improvements.basement?.hasFeature,
                }),
              unknownLabel('basement.finishedArea') ??
                formatFeatureArea({
                  area: improvements.basement?.finishedArea?.unitAmount,
                  unit: improvements.basement?.finishedArea?.unit,
                  hasFeature: improvements.basement?.hasFeature,
                }),
            ]
              .filter(Boolean)
              .join('/'),
          ]
            .filter(Boolean)
            .join('; '),
      basementFullBaths,
      basementHalfBaths,
      garage: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['carport', 'detachedGarage', 'garage'],
        featureDefs: [ORDER_FEATURES.carport, ORDER_FEATURES.detachedGarage, ORDER_FEATURES.garage],
        unknownEarly: () => {
          const featureFieldsPaths = [
            'carport.quality',
            'detachedGarage.quality',
            'garage.quality',
          ];

          if (
            featureFieldsPaths.some((suffix) =>
              isExplicitUnknownSelectedForFieldPath(
                selectedSources,
                `${improvementsPathPrefix}.${suffix}`,
              ),
            )
          ) {
            return EXPLICIT_UNKNOWN_SOURCE_DISPLAY;
          }
        },
        format: () => getAbbreviatedCarportGarage(improvements) ?? '',
      }),
      porch: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['porch', 'patio', 'deck'],
        featureDefs: [ORDER_FEATURES.porch, ORDER_FEATURES.patio, ORDER_FEATURES.deck],
        format: () => {
          if (!includeCostApproach) {
            return getPresentFeatureLabels(improvements, ['porch', 'patio', 'deck']);
          }
          return (
            [
              ['porch', 'Porch'],
              ['patio', 'Patio'],
              ['deck', 'Deck'],
            ] as const
          )
            .map(([feat, typeLabel]) => {
              if (
                isExplicitUnknownSelectedForFieldPath(
                  selectedSources,
                  `${improvementsPathPrefix}.${feat}.area`,
                )
              ) {
                return EXPLICIT_UNKNOWN_SOURCE_DISPLAY;
              }
              return formatFeatureArea({
                area: improvements[feat]?.area?.unitAmount,
                unit: improvements[feat]?.area?.unit,
                type: typeLabel,
                hasFeature: improvements[feat]?.hasFeature,
              });
            })
            .filter(Boolean)
            .join('; ');
        },
      }),
      attic: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['attic'],
        featureDefs: [ORDER_FEATURES.attic],
        unknownEarly: () =>
          isExplicitUnknownSelectedForFieldPath(
            selectedSources,
            `${improvementsPathPrefix}.attic.area`,
          )
            ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
            : undefined,
        format: () =>
          [
            getBasementAtticTypeAbbreviation(improvements.attic?.type),
            formatFeatureArea({
              area: improvements.attic?.area?.unitAmount,
              unit: improvements.attic?.area?.unit,
              hasFeature: improvements.attic?.hasFeature,
            }),
          ]
            .filter(Boolean)
            .join('; '),
      }),
      fireplace: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['fireplace'],
        featureDefs: [ORDER_FEATURES.fireplace],
        unknownEarly: () =>
          isExplicitUnknownSelectedForFieldPath(
            selectedSources,
            `${improvementsPathPrefix}.fireplace.type`,
          ) ||
          isExplicitUnknownSelectedForFieldPath(
            selectedSources,
            `${improvementsPathPrefix}.fireplace.count`,
          )
            ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
            : undefined,
        format: () =>
          improvements.fireplace?.count && improvements.fireplace.hasFeature
            ? `${improvements.fireplace.count} ${improvements.fireplace.type ?? ''}`.trim()
            : '',
      }),
      pool: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['pool', 'spa'],
        featureDefs: [ORDER_FEATURES.pool, ORDER_FEATURES.spa],
        format: () => {
          if (!includeCostApproach) {
            return getPresentFeatureLabels(improvements, ['pool', 'spa']);
          }
          return (
            [
              ['pool', 'Pool'],
              ['spa', 'Spa'],
            ] as const
          )
            .map(([feat, typeLabel]) => {
              if (
                isExplicitUnknownSelectedForFieldPath(
                  selectedSources,
                  `${improvementsPathPrefix}.${feat}.area`,
                )
              ) {
                return EXPLICIT_UNKNOWN_SOURCE_DISPLAY;
              }

              const areaLabel = formatFeatureArea({
                area: improvements[feat]?.area?.unitAmount,
                unit: improvements[feat]?.area?.unit,
                type: typeLabel,
                hasFeature: improvements[feat]?.hasFeature,
              });

              if (!areaLabel) return areaLabel;

              const typeAbbreviation =
                feat === 'pool' ? getPoolTypeAbbreviation(improvements.pool?.type) : undefined;
              return typeAbbreviation ? `${typeAbbreviation}; ${areaLabel}` : areaLabel;
            })
            .filter(Boolean)
            .join('; ');
        },
      }),
      shed: resolveOrderKeyedAreaFeaturePdfCell(
        improvements,
        orderFeatures,
        'shed',
        selectedSources,
        improvementsPathPrefix,
        includeCostApproach,
      ),
      barn: resolveOrderKeyedAreaFeaturePdfCell(
        improvements,
        orderFeatures,
        'barn',
        selectedSources,
        improvementsPathPrefix,
        includeCostApproach,
      ),
      adu: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['adu'],
        featureDefs: [ORDER_FEATURES.adu],
        unknownEarly: () =>
          includeCostApproach &&
          isExplicitUnknownSelectedForFieldPath(
            selectedSources,
            `${improvementsPathPrefix}.adu.area`,
          )
            ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
            : undefined,
        format: () => {
          if (!includeCostApproach) {
            return getPresentFeatureLabels(improvements, ['adu']);
          }
          const areaString = formatFeatureArea({
            area: improvements.adu?.area?.unitAmount,
            unit: improvements.adu?.area?.unit,
            hasFeature: improvements.adu?.hasFeature,
          });
          const bathsString = getAduBathsAbbreviation(improvements.adu);
          return [areaString, bathsString].filter(Boolean).join('; ');
        },
      }),
      workshop: resolveOrderKeyedAreaFeaturePdfCell(
        improvements,
        orderFeatures,
        'workshop',
        selectedSources,
        improvementsPathPrefix,
        includeCostApproach,
      ),
      solarPanel: resolveOrderKeyedFeaturePdfCell(improvements, orderFeatures, {
        orderKeys: ['solarPanel'],
        featureDefs: [ORDER_FEATURES.solarPanel],
        unknownEarly: () =>
          isExplicitUnknownSelectedForFieldPath(
            selectedSources,
            `${improvementsPathPrefix}.solarPanel.ownership`,
          )
            ? EXPLICIT_UNKNOWN_SOURCE_DISPLAY
            : undefined,
        format: () => {
          if (!improvements.solarPanel || !improvements.solarPanel?.hasFeature) {
            return '';
          }
          if (!improvements.solarPanel.ownership) {
            return 'Yes';
          }

          return improvements.solarPanel.ownership === 'leased' ? 'Leased' : 'Owned';
        },
      }),
    },
    manualCostDisplayName: improvements.manualCostDisplayName?.trim() ?? '',
    manualCostSummary: (() => {
      const parsed = orUndefined(schAloftPrice.parse)(manualAdjustments?.manual);
      if (parsed !== undefined) return parsed.toString(true);
      if (orderFeatures?.includes('manualCost')) return AloftPrice.zero.toString(true);
      return '';
    })(),
    totalCostToCureDisplayName: improvements?.totalCostToCureDisplayName ?? 'None',
    otherAdjustmentsDisplayName:
      improvements?.otherAdjustmentsDisplayName?.trim() ||
      (id === 'subject' || id === 'subjectArv' ? '' : 'None'),
    netAdj: adjustments?.netAdjustmentPct?.value ?? '',
    grossAdj: adjustments?.grossAdjustmentPct?.value ?? '',
    adjSalesPrice: adjustments?.adjustedSalePrice?.adjustment,
    adjustments: {
      saleDateAdjustment: adjustments?.dateOfSale?.adjustment,
      siteSizeAdjustment: adjustments?.site?.size?.adjustment,
      siteInfluenceAdjustment: adjustments?.siteInfluence?.adjustment,
      siteViewAdjustment: adjustments?.siteView?.adjustment,
      qualityAdjustment: adjustments?.quality?.adjustment,
      conditionAdjustment: adjustments?.condition?.adjustment,
      glaAdjustment: adjustments?.mainDwelling?.gla?.adjustment,
      bedCountAdjustment: adjustments?.bedrooms?.adjustment,
      actualAgeAdjustment: adjustments?.actualAge?.adjustment,
      fullBathsAdjustment: adjustments?.fullBaths?.adjustment,
      halfBathsAdjustment: adjustments?.halfBaths?.adjustment,
      bathAboveGradeAdjustment: adjustments?.bathAboveGrade?.adjustment,
      basementBathsAdjustment: adjustments?.bathBelowGrade?.adjustment,
      basementTotalAdjustment: adjustments?.basementTotal?.adjustment,
      basementFinishedAdjustment: adjustments?.basementFinished?.adjustment,
      basementAreaAdjustment: adjustments?.basement?.adjustment,
      basementHalfBathsAdjustment: adjustments?.basementHalfBaths?.adjustment,
      basementFullBathsAdjustment: adjustments?.basementFullBaths?.adjustment,
      garageAdjustment: adjustments?.garageCarport?.adjustment,
      porchAdjustment: adjustments?.porchPatioDeck?.adjustment,
      atticAdjustment: adjustments?.attic?.adjustment,
      fireplaceAdjustment: adjustments?.fireplace?.adjustment,
      solarPanelAdjustment: adjustments?.solarPanel?.adjustment,
      poolAdjustment: adjustments?.pool?.adjustment,
      shedAdjustment: adjustments?.shed?.adjustment,
      barnAdjustment: adjustments?.barn?.adjustment,
      aduAdjustment: adjustments?.adu?.adjustment,
      workshopAdjustment: adjustments?.workshop?.adjustment,
      manualCostAdjustment: adjustments?.manual?.adjustment,
      totalCostToCureAdjustment: adjustments?.totalCostToCure?.adjustment,
      otherAdjustments,
      netAdjPrice: adjustments?.netAdjustment?.adjustment,
    },
    explicitZeroAdjustments,
    compType: id === 'subject' ? undefined : (data as CompPropertyDataState['compData'])?.compType,
    position: compSequence.indexOf(id) + 1,
    actualAge,
    propertyType: formatShortenedPropertyType(improvements.propertyType) ?? '',
  };
};
